import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import Experiment, { When } from '@carvana/experiment';
import 'url-search-params-polyfill';
import { initializeHaaP } from '@Header/Navigation/ducks/actions';
import { fetchPurchaseDetails } from '@Header/Account/ducks/operations';
import { openModal } from '@Header/Modals/ducks/actions';
import { handleDelayedModals } from '@Header/Modals/ducks/operations';
import { AnalyticsContext } from '@carvana/eventable-clickstream';
import {
  getDiscounts,
  getShouldFireHeaderEventablePageLoadEvent,
  handlePromotions,
  setUserProfile
} from './ducks/operations';
import { setAccountUrls, setAppSettings, setCampaignPromotions, setGeolocation, setIsInDotCom } from './ducks/actions';
import { checkIfMobile, getScreenHeight } from './ducks/reducers';
import ApplicationSettings from '../services/ApplicationSettings';
import Geolocation from '../services/Geolocation';
import HeaderApi from '../services/HeaderApi';
import GoogleTagManagerClient from '../services/GoogleTagManagerClient';
import Cookie from '../services/Cookie';
import LocalStorage from '../services/LocalStorage';
import {
  GetActiveMenuName,
  isCarvanaMobileAppUserAgent,
  IsPurchaseProcess,
  setHasOptedOutOfThirdPartyDataSharing
} from '../utilities';
import HeaderMenu from './HeaderMenu';
import HeaderMenuMobile from './HeaderMenu/Mobile';
import Navigation from './Navigation';
import Logo from './Logo';
import Account from './Account';
import Banners from './Banners';
import Partner from './Partner';
import { Favorites } from './Favorites/Favorites';
import Modals from './Modals';
import ChatUi from './ChatUi';

import { userNotificationOperations } from './UserNotifications/ducks';
import { outageBannerOperations } from './Banners/OutageBanner/ducks';
import DeviceDetectionApi from '../services/DeviceDetectionApi';
import { HeaderMenuMobileWrapper, HeaderMenuWrapper, HeaderWrapper, MobileLogo } from './Header.styles';
import {
  getDiscounts as getDiscountsSelector,
  getGeolocation,
  getHeaderBannersAndModals,
  getShouldFireHeaderEventablePageLoadFeatureFlag,
} from './ducks/selectors';

class Header extends PureComponent {
  static contextType = AnalyticsContext;

  static propTypes = {
    profile: PropTypes.shape({}).isRequired,
    environment: PropTypes.string.isRequired,
    fetchPurchaseDetails: PropTypes.func.isRequired,
    setProfile: PropTypes.func.isRequired,
    isMobile: PropTypes.bool.isRequired,
    screenHeight: PropTypes.number.isRequired,
    useModalUrlForAccountLinks: PropTypes.bool.isRequired,
    setAccountUrls: PropTypes.func.isRequired,
    forceMobileLayout: PropTypes.bool.isRequired,
    setGeolocation: PropTypes.func.isRequired,
    setAppSettings: PropTypes.func.isRequired,
    handlePromotions: PropTypes.func.isRequired,
    handleDelayedModals: PropTypes.func.isRequired,
    initializeHaaP: PropTypes.func.isRequired,
    purchaseProcessVehiclePrice: PropTypes.number.isRequired,
    currentRoute: PropTypes.string.isRequired,
    getUserNotifications: PropTypes.func.isRequired,
    setIsInDotCom: PropTypes.func.isRequired,
    getDiscounts: PropTypes.func.isRequired,
    setCampaignPromotions: PropTypes.func.isRequired,
    fetchOutageBannerDetails: PropTypes.func.isRequired,
    favorites: PropTypes.shape({
      favorites: PropTypes.arrayOf(PropTypes.shape({})),
      removeFavorite: PropTypes.func
    }),
    skeletonState: PropTypes.shape({}),
    isInDotCom: PropTypes.bool,
    discounts: PropTypes.arrayOf(PropTypes.shape({})),
    headerContext: PropTypes.shape({
      accountModal: PropTypes.shape({}),
    }),
    fetchShouldFireHeaderEventablePageLoadEvent: PropTypes.func.isRequired,
    shouldFireEventablePageLoadEvent: PropTypes.bool.isRequired,
    headerBannersAndModals: PropTypes.shape({
      promotions: PropTypes.array
    }).isRequired,
    geolocation: PropTypes.shape({})
  };

  static defaultProps = {
    favorites: {},
    skeletonState: {
      isHowItWorksExpanded: false,
      isAboutCarvanaExpanded: false,
      isFinancingExpanded: false
    },
    isInDotCom: false,
    discounts: [],
    headerContext: {}
  };

  static getDerivedStateFromProps(props, state) {
    if (state.activeMenuName === null) {
      return {
        activeMenuName: GetActiveMenuName()
      };
    }
    return {};
  }

  constructor(props) {
    super(props);
    const { profile, environment, skeletonState, fetchShouldFireHeaderEventablePageLoadEvent } = props;
    const {
      setGeolocation: setGeolocationAction,
      setAppSettings: setAppSettingsAction,
      initializeHaaP: getHaapInitialState
    } = this.props;

    ApplicationSettings.setEnvironment(environment);
    Geolocation.setGeolocation().then(res => {
      setGeolocationAction(res);
      HeaderApi.fetchAppSettings(['HeaderBannersAndModals', 'HeaderSupportConfig']).then(setAppSettingsAction);
    });

    GoogleTagManagerClient.setup();
    getHaapInitialState(skeletonState);

    const deviceInformation = LocalStorage.getJSON('deviceInformation');
    if (!deviceInformation) {
      DeviceDetectionApi.fetchDeviceInformation()
        .then(deviceInfo => this.deviceDetected(deviceInfo))
        .catch(() => {});
    }

    this.state = {
      activeMenuName: null
    };

    if (profile && profile.isAuthorized) {
      this.initOnLogin();
      setHasOptedOutOfThirdPartyDataSharing(profile);
      return;
    }
    setHasOptedOutOfThirdPartyDataSharing();
    fetchShouldFireHeaderEventablePageLoadEvent();
  }

  deviceDetected(deviceInformation) {
    const now = new Date();
    const expiryTimestamp = new Date(now.setDate(now.getDate() + 30));
    LocalStorage.setJSON('deviceInformation', deviceInformation, expiryTimestamp);
  }

  componentDidMount() {
    const {
      setAccountUrls: setAccountUrlsAction,
      useModalUrlForAccountLinks,
      setIsInDotCom: setIsInDotComAction,
      isInDotCom,
      fetchOutageBannerDetails
    } = this.props;
    setAccountUrlsAction(useModalUrlForAccountLinks);
    setIsInDotComAction(isInDotCom);
    fetchOutageBannerDetails();
  }

  initOnLogin = () => {
    const {
      getUserNotifications,
      setProfile: setProfileAction,
      profile,
      getDiscounts: getDiscountsAction,
      handlePromotions: handlePromotionsAction,
      headerBannersAndModals
    } = this.props;

    if (profile.isLoggedIn) {
      getUserNotifications();
    }
    setProfileAction(profile);

    const { userProfile } = profile;
    const { profileAddressState, profileZip } = userProfile || {};
    const stateCookie = Cookie.getCookie('CVCurrentState');
    const zipCookie = Cookie.getCookie('CVCurrentZip');
    const userState = profileAddressState || stateCookie;
    const userZip = profileZip || zipCookie;
    if (profile.isLoggedIn && userState) {
      getDiscountsAction(userState);
      handlePromotionsAction(headerBannersAndModals, {
        state: userState,
        zip5: userZip
      });
    }
  };

  componentDidUpdate(prevProps) {
    const { trackEvent } = this.context;
    const {
      profile,
      currentRoute,
      discounts,
      shouldFireEventablePageLoadEvent,
      headerBannersAndModals,
      handleDelayedModals: handleDelayedModalsAction,
      handlePromotions: handlePromotionsAction,
      geolocation
    } = this.props;

    if (prevProps.shouldFireEventablePageLoadEvent !== shouldFireEventablePageLoadEvent) {
      trackEvent('carvana-page-load');
    }

    if (prevProps.profile !== profile) {
      this.initOnLogin();
      setHasOptedOutOfThirdPartyDataSharing(profile);
    }

    if (prevProps.headerBannersAndModals !== headerBannersAndModals) {
      handlePromotionsAction(headerBannersAndModals, geolocation);
      handleDelayedModalsAction();
    }

    const hasRouteChanged = currentRoute !== prevProps.currentRoute;
    if (IsPurchaseProcess() && hasRouteChanged) {
      this.props.fetchPurchaseDetails('purchase/details');
    }

    const hasDiscounts = discounts !== prevProps.discounts;
    if (hasDiscounts) {
      this.triggerCampaignPromotions();
    }
  }

  triggerCampaignPromotions = () => {
    // note(oscar); discounts is used to determine which promotions to display to the customer. Where are those promotions displayed?
    const { discounts, setCampaignPromotions: setCampaignPromotionsAction } = this.props;
    if (!discounts || !discounts.length || discounts.length < 1) return;
    discounts.forEach(discount => {
      const { type } = discount;
      if (type === 'Campaign') {
        setCampaignPromotionsAction();
      }
    });
  };

  render() {
    const {
      profile,
      isMobile,
      screenHeight,
      forceMobileLayout,
      environment,
      favorites,
      purchaseProcessVehiclePrice,
      currentRoute,
      headerContext: { accountModal }
    } = this.props;
    const isMobileApp = isCarvanaMobileAppUserAgent();
    if (isMobileApp) return null;

    const partner = profile.partner || {};
    const { activeMenuName } = this.state;

    const userId = (profile && profile.userProfile && profile.userProfile.userId) || '';

    const hasPartner = partner && partner.shouldHidePartnerHeaderBanner !== undefined;
    const shouldShowPartnerBanner = hasPartner && !partner.shouldHidePartnerHeaderBanner;
    const shouldShowMobileMenu = isMobile || forceMobileLayout;
    const shouldShowCarvanaFinancing = true;

    return (
      <HeaderWrapper data-cv-test="headerWrapper">
        <Banners environment={environment} userId={userId} />
        <Navigation environment={environment} />
        {shouldShowPartnerBanner ? <Partner partner={partner} /> : null}

        <HeaderMenuWrapper>
          <Logo />
          <HeaderMenu
            partner={partner}
            activeMenuName={activeMenuName}
            shouldShowCarvanaFinancing={shouldShowCarvanaFinancing}
            isLoggedIn={profile.isLoggedIn}
          />
          {profile.isLoggedIn && <Favorites {...(favorites || {})} />}
          <Account purchaseProcessVehiclePrice={purchaseProcessVehiclePrice} />
        </HeaderMenuWrapper>

        {shouldShowMobileMenu && (
          <HeaderMenuMobileWrapper>
            <MobileLogo isMobile />
            {profile.isLoggedIn && <Favorites {...(favorites || {})} />}

            <HeaderMenuMobile
              screenHeight={screenHeight}
              partner={partner}
              shouldShowCarvanaFinancing={shouldShowCarvanaFinancing}
              purchaseProcessVehiclePrice={purchaseProcessVehiclePrice}
              isLoggedIn={profile.isLoggedIn}
            />
          </HeaderMenuMobileWrapper>
        )}

        <Modals environment={environment} userId={userId} headerContextAccountModal={accountModal} />

        <ChatUi />

        <Experiment name="aa-test-with-alt-id-v2">
          <When bucket="control" />
        </Experiment>
      </HeaderWrapper>
    );
  }
}

const mapDispatchToProps = dispatch => ({
  setProfile: partner => setUserProfile(partner)(dispatch),
  setAccountUrls: useModalUrlForAccountLinks => dispatch(setAccountUrls(useModalUrlForAccountLinks)),
  setGeolocation: geolocation => dispatch(setGeolocation(geolocation)),
  setAppSettings: appSettings => dispatch(setAppSettings(appSettings)),
  handlePromotions: (headerBannersAndModals, geolocation) =>
    handlePromotions(headerBannersAndModals, geolocation)(dispatch),
  handleDelayedModals: () => dispatch(handleDelayedModals()),
  initializeHaaP: skeletonState => dispatch(initializeHaaP(skeletonState)),
  getUserNotifications: () => dispatch(userNotificationOperations.getUserNotifications()),
  setIsInDotCom: isInDotCom => dispatch(setIsInDotCom(isInDotCom)),
  // note(oscar): this is the only place where discounts appear; what is it used for?
  getDiscounts: stateAbbreviation => dispatch(getDiscounts(stateAbbreviation)),
  setCampaignPromotions: () => dispatch(setCampaignPromotions()),
  openModal: modalName => dispatch(openModal(modalName)),
  fetchPurchaseDetails: url => dispatch(fetchPurchaseDetails(url)),
  fetchOutageBannerDetails: () => dispatch(outageBannerOperations.getOutageBannerDetails()),
  fetchShouldFireHeaderEventablePageLoadEvent: () => dispatch(getShouldFireHeaderEventablePageLoadEvent())
});

const mapStateToProps = state => ({
  isMobile: checkIfMobile(state),
  screenHeight: getScreenHeight(state),
  discounts: getDiscountsSelector(state),
  shouldFireEventablePageLoadEvent: getShouldFireHeaderEventablePageLoadFeatureFlag(state),
  headerBannersAndModals: getHeaderBannersAndModals(state),
  geolocation: getGeolocation(state)
});

export default connect(mapStateToProps, mapDispatchToProps)(Header);
